import { useState } from 'react';
import { Stack, Typography, Theme, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from '../../config/dayjs';
import { TitleWithBackButton } from '../../components/common/TitleWithBackButton/TitleWithBackTutton';
import {
  useNotificationsQuery,
  useMarkAsRead,
  useMarkAllAsRead,
} from '../../hooks/useNotifications';
import { AppNotification } from '../../types/notification';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';

function NotificationCard({
  notification,
  onRead,
}: {
  notification: AppNotification;
  onRead: (id: string) => void;
}) {
  const isUnread = !notification.read_at;
  const plainMessage = notification.message.replace(/<[^>]*>/g, '');

  return (
    <Stack
      onClick={() => isUnread && onRead(notification.id)}
      gap="4px"
      sx={(theme: Theme) => ({
        borderRadius: '15px',
        padding: '12px 14px',
        border: `1px solid ${theme.palette.gray[200]}`,
        cursor: isUnread ? 'pointer' : 'default',
        background: isUnread ? 'rgba(36, 237, 164, 0.05)' : 'transparent',
        transition: 'background 0.15s',
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography
          variant="body1"
          color="common.white"
          sx={{ fontWeight: isUnread ? 600 : 400 }}
        >
          {notification.title}
        </Typography>
        {isUnread && (
          <Stack
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#24EDA4',
              flexShrink: 0,
            }}
          />
        )}
      </Stack>

      <Typography variant="body1" sx={{ color: 'gray.50', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
        {plainMessage}
      </Typography>

      <Typography sx={{ fontSize: 11, color: 'gray.50' }}>
        {dayjs(notification.created_at).fromNow()}
      </Typography>
    </Stack>
  );
}

export const CabinetNotifications = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'all' | 'unread'>('all');
  const { page, rowsPerPage, setPage, setRowsPerPage, resetPage } = usePaginationState(25);

  const { data, isLoading } = useNotificationsQuery(page + 1, rowsPerPage);
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllAsRead();

  const allNotifications = data?.data ?? [];
  const notifications = tab === 'unread' ? allNotifications.filter((n) => !n.read_at) : allNotifications;
  const unreadCount = data?.unreadCount ?? 0;
  const total = tab === 'unread' ? unreadCount : (data?.total ?? 0);

  return (
    <Stack spacing={3} sx={containerStyles}>
      <TitleWithBackButton title={t('notificationsPage.title')} position="center" />

      {/* Tabs */}
      <Stack direction="row" sx={tabsContainerStyles}>
        <TabButton active={tab === 'all'} onClick={() => { setTab('all'); resetPage(); }}>
          {t('notificationsPage.tabAll')}
        </TabButton>
        <TabButton active={tab === 'unread'} onClick={() => { setTab('unread'); resetPage(); }}>
          {t('notificationsPage.tabUnread')}{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </TabButton>
      </Stack>

      {/* Mark all read */}
      {unreadCount > 0 && (
        <Stack alignItems="flex-end">
          <Typography
            component="button"
            onClick={() => markAllAsRead()}
            disabled={isMarkingAll}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#24EDA4',
              fontSize: 13,
              fontWeight: 500,
              p: 0,
              opacity: isMarkingAll ? 0.5 : 1,
            }}
          >
            {t('notificationsPage.markAllRead')}
          </Typography>
        </Stack>
      )}

      {/* Content */}
      {isLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={28} sx={{ color: '#24EDA4' }} />
        </Stack>
      ) : notifications.length === 0 ? (
        <Stack alignItems="center" gap={2} py={4}>
          <Typography variant="body1" color="common.white" textAlign="center">
            {t('notificationsPage.empty')}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          {notifications.map((n: AppNotification) => (
            <NotificationCard key={n.id} notification={n} onRead={markAsRead} />
          ))}
        </Stack>
      )}

      <Pagination
        tone="dark"
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </Stack>
  );
};

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Typography
      component="button"
      onClick={onClick}
      sx={(theme: Theme) => ({
        flex: 1,
        py: '10px',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid #24EDA4' : `2px solid ${theme.palette.gray[200]}`,
        cursor: 'pointer',
        color: active ? '#24EDA4' : theme.palette.gray[50],
        fontWeight: active ? 600 : 400,
        fontSize: 14,
        transition: 'all 0.15s',
      })}
    >
      {children}
    </Typography>
  );
}

const containerStyles = {
  padding: '14px 22px',
  maxWidth: 400,
  width: '100%',
};

const tabsContainerStyles = {
  borderBottom: 'none',
};
